import React, { useState } from 'react'
import { Button, Toast } from 'components'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { bulkUpdateFleets } from 'api'
import { IValidationTypeData, ValidationMode } from 'interfaces/IFleetMonitoring'
import strings from 'strings'

const FM = strings.FLEET_MONITORING

interface IBulkOverrideModalProps {
  open: boolean
  carrierIds: number[]
  validationTypes: IValidationTypeData[]
  onClose: () => void
  onApplied: () => void
}

const BulkOverrideModal: React.FunctionComponent<IBulkOverrideModalProps> = ({
  open,
  carrierIds,
  validationTypes,
  onClose,
  onApplied,
}) => {
  const [validationCode, setValidationCode] = useState<string>(validationTypes[0]?.code || '')
  const [mode, setMode] = useState<ValidationMode>('inherit')
  const [submitting, setSubmitting] = useState(false)

  React.useEffect(() => {
    if (open && validationTypes.length > 0) {
      setValidationCode(validationTypes[0].code)
      setMode('inherit')
    }
  }, [open, validationTypes])

  const handleApply = async () => {
    if (!validationCode) return
    setSubmitting(true)
    try {
      await bulkUpdateFleets({
        carrierIds,
        operation: 'setValidationMode',
        payload: { validationTypeCode: validationCode, mode },
      })
      Toast({
        type: 'success',
        subTitle: FM.TOASTS.OVERRIDE_APPLIED(carrierIds.length),
      })
      onApplied()
      onClose()
    } catch (e: any) {
      Toast({ type: 'error', subTitle: e?.message || FM.ERRORS.APPLY_OVERRIDE_FAILED })
    } finally {
      setSubmitting(false)
    }
  }

  const count = carrierIds.length
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {FM.BULK_OVERRIDE.TITLE}
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {FM.BULK_OVERRIDE.APPLYING_TO(count)}
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>{FM.BULK_OVERRIDE.VALIDATION_TYPE}</InputLabel>
          <Select
            label={FM.BULK_OVERRIDE.VALIDATION_TYPE}
            value={validationCode}
            onChange={e => setValidationCode(e.target.value)}
          >
            {validationTypes.map(vt => (
              <MenuItem key={vt.code} value={vt.code}>
                {vt.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {FM.BULK_OVERRIDE.MODE_LABEL}
        </Typography>
        <RadioGroup value={mode} onChange={e => setMode(e.target.value as ValidationMode)}>
          <FormControlLabel
            value="inherit"
            control={<Radio size="small" />}
            label={FM.BULK_OVERRIDE.MODE_INHERIT}
          />
          <FormControlLabel
            value="on"
            control={<Radio size="small" />}
            label={FM.BULK_OVERRIDE.MODE_ON}
          />
          <FormControlLabel
            value="off"
            control={<Radio size="small" />}
            label={FM.BULK_OVERRIDE.MODE_OFF}
          />
        </RadioGroup>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button label={FM.COMMON.CANCEL} variant="text" onClick={onClose} disabled={submitting} />
        <Button label={FM.BULK_OVERRIDE.APPLY_TO(count)} variant="contained" onClick={handleApply} disabled={submitting || !validationCode} />
      </DialogActions>
    </Dialog>
  )
}

export default BulkOverrideModal
